import { createAsyncThunk } from '@reduxjs/toolkit';
import ImporterService from '@/services/ImporterService';
import { USE_IMPORTER_MOCK } from '@/services/importerMockConfig';
import {
  buildJobsFilter,
  JOB_STATUS,
} from '@/components/Company/importer/constants';
import { buildStartSettingsPayload } from '@/components/Company/importer/importSettings';
import {
  buildCrmInstancesPayload,
  hasSelectedCrmEntities,
  primaryCrmEntity,
  selectedCrmEntities,
} from '@/components/Company/importer/crmInstances';
import { CRM_MODE } from '@/components/Company/importer/wizard/importerFlow';
import {
  isApiPortalOrigin,
  isCrmApiOrigin,
} from '@/components/Company/importer/wizard/originOptions';
import {
  buildAgentMappingsBody,
  buildLeadStatusMappingsBody,
  normalizeJobsResponse,
  normalizeLiveInfo,
  normalizeValueMappings,
} from '@/components/Company/importer/normalize';
import {
  applyTargetFieldChange,
  buildMappingPayload,
  ignoreReviewRows,
  normalizeMapping,
} from '@/components/Company/importer/mappingModel';
import { getMockJobsResponse } from './mock/importerJobs.fixture';
import {
  contactsExportFilename,
  triggerBlobDownload,
} from '@/components/Company/importer/contactsExport';

const rejectApiError = (error) => ({
  message: error.message,
  reason: error.response?.reason ?? null,
});

export const fetchJobsAsync = createAsyncThunk(
  'Importer/fetchJobs',
  async ({ page = 1, statusFilter = 'all' } = {}) => {
    if (USE_IMPORTER_MOCK) {
      // The fixture already matches the view model, so no adapting is needed.
      return getMockJobsResponse({ page, statusFilter });
    }
    // Adapt the raw backend contract to the view model consumed by the UI.
    const response = await ImporterService.getJobs(
      page,
      buildJobsFilter(statusFilter)
    );
    return normalizeJobsResponse(response);
  }
);

export const fetchJobLiveInfoAsync = createAsyncThunk(
  'Importer/fetchJobLiveInfo',
  async (id, { dispatch, getState }) => {
    // Polling only makes sense against the live backend; the mock is static.
    if (USE_IMPORTER_MOCK) {
      return null;
    }
    const raw = normalizeLiveInfo(await ImporterService.getJob(id));
    // Merge on the id we requested, not live_info.import_id, so the key always
    // matches the list row (guards against a missing/differently-typed id).
    const liveInfo = raw ? { ...raw, id } : null;
    if (liveInfo) {
      const { jobs, page, statusFilter } = getState().ImporterSlice;
      const previous = jobs.find((job) => job.id === liveInfo.id);
      // When a job leaves the in-progress state, refresh the list so the KPI
      // counts and the completed row reflect the backend's source of truth.
      if (
        previous?.status === JOB_STATUS.IN_PROGRESS &&
        liveInfo.status &&
        liveInfo.status !== JOB_STATUS.IN_PROGRESS
      ) {
        dispatch(fetchJobsAsync({ page, statusFilter }));
      }
    }
    return liveInfo;
  }
);

export const retryJobAsync = createAsyncThunk(
  'Importer/retryJob',
  async (id, { dispatch, getState }) => {
    if (!USE_IMPORTER_MOCK) {
      await ImporterService.retryJob(id);
    }
    const { page, statusFilter } = getState().ImporterSlice;
    await dispatch(fetchJobsAsync({ page, statusFilter }));
    return id;
  }
);

export const downloadContactsExportAsync = createAsyncThunk(
  'Importer/downloadContactsExport',
  async ({ id, entityType }, { rejectWithValue }) => {
    try {
      const blob = await ImporterService.downloadContactsExport(id);
      triggerBlobDownload(blob, contactsExportFilename(id, entityType));
      return id;
    } catch (error) {
      return rejectWithValue(rejectApiError(error));
    }
  }
);

export const fetchCrmRequiredFilesAsync = createAsyncThunk(
  'Importer/fetchCrmRequiredFiles',
  async (_, { getState, rejectWithValue }) => {
    const { crmInstances, origin } = getState().ImporterSlice;
    try {
      const response = await ImporterService.getCrmRequiredFiles(
        origin,
        buildCrmInstancesPayload(crmInstances)
      );
      return response?.required ?? [];
    } catch (error) {
      return rejectWithValue(rejectApiError(error));
    }
  },
  {
    condition: (_, { getState }) => {
      const { crmInstances, crmRequiredStatus } = getState().ImporterSlice;
      return (
        hasSelectedCrmEntities(crmInstances) && crmRequiredStatus !== 'loading'
      );
    },
  }
);

// Persists the whole entity selection on the draft job created by the upload,
// which is also when the backend validates the bundle and counts its records.
export const validateApiCredentialsAsync = createAsyncThunk(
  'Importer/validateApiCredentials',
  async (_, { getState, rejectWithValue }) => {
    const { origin, apiKey } = getState().ImporterSlice;
    try {
      const response = await ImporterService.validateApiCredentials({
        origin,
        apiKey: apiKey.trim(),
      });
      if (response?.ok !== true) {
        return rejectWithValue({
          message: response?.message ?? null,
          reason: response?.reason ?? 'api_connection_error',
        });
      }
      return null;
    } catch (error) {
      return rejectWithValue(rejectApiError(error));
    }
  },
  {
    condition: (_, { getState }) => {
      const { apiCredentialsStatus, apiKey } = getState().ImporterSlice;
      return apiCredentialsStatus !== 'loading' && Boolean(apiKey.trim());
    },
  }
);

export const configureApiCrmJobAsync = createAsyncThunk(
  'Importer/configureApiCrmJob',
  async (_, { getState, rejectWithValue }) => {
    const { entity, origin, apiKey, jobId, crmInstances } =
      getState().ImporterSlice;
    const selectedEntities = selectedCrmEntities(crmInstances);
    let uploadEntities = selectedEntities;
    if (uploadEntities.length === 0 && entity) {
      uploadEntities = [entity];
    }
    try {
      return await ImporterService.uploadFiles({
        files: [],
        entities: uploadEntities,
        origin,
        jobId,
        apiKey: apiKey.trim(),
      });
    } catch (error) {
      return rejectWithValue(rejectApiError(error));
    }
  },
  {
    condition: (_, { getState }) => {
      const { apiCredentialsStatus, uploadStatus } = getState().ImporterSlice;
      return apiCredentialsStatus === 'succeeded' && uploadStatus !== 'loading';
    },
  }
);

export const configureCrmJobAsync = createAsyncThunk(
  'Importer/configureCrmJob',
  async (_, { getState, rejectWithValue }) => {
    const { jobId, crmInstances } = getState().ImporterSlice;
    try {
      return await ImporterService.saveCrmConfig(
        jobId,
        buildCrmInstancesPayload(crmInstances)
      );
    } catch (error) {
      return rejectWithValue(rejectApiError(error));
    }
  },
  {
    condition: (_, { getState }) => {
      const { jobId, crmConfigStatus } = getState().ImporterSlice;
      return Boolean(jobId) && crmConfigStatus !== 'loading';
    },
  }
);

export const uploadFilesAsync = createAsyncThunk(
  'Importer/uploadFiles',
  async (_, { getState, rejectWithValue }) => {
    const { entity, origin, files, jobId, mode, crmInstances } =
      getState().ImporterSlice;
    // A CRM bundle covers several entities, but the draft job is created with a
    // single one; `crm-config` replaces it with the full selection afterwards.
    const uploadEntity =
      mode === CRM_MODE ? primaryCrmEntity(crmInstances) ?? entity : entity;
    try {
      return await ImporterService.uploadFiles({
        files,
        entity: uploadEntity,
        origin,
        jobId,
      });
    } catch (error) {
      return rejectWithValue({
        message: error.message,
        reason: error.response?.reason ?? null,
      });
    }
  },
  {
    // Guard against duplicate in-flight uploads from a quick double activation,
    // and against an empty request when dropping a file that is already
    // uploaded leaves nothing new to send.
    condition: (_, { getState }) => {
      const { uploadStatus, files, origin } = getState().ImporterSlice;
      return (
        uploadStatus !== 'loading' &&
        (files.length > 0 || isApiPortalOrigin(origin))
      );
    },
  }
);

export const startImportAsync = createAsyncThunk(
  'Importer/startImport',
  async (_, { getState, rejectWithValue }) => {
    const {
      jobId,
      entity,
      mode,
      origin,
      importSettingsDraft,
      importWithoutImages,
    } = getState().ImporterSlice;
    let body;
    if (mode === CRM_MODE) {
      body = isCrmApiOrigin(origin)
        ? { duplicate_policy: 'update' }
        : { import_without_images: importWithoutImages };
    } else {
      const policies = buildStartSettingsPayload(importSettingsDraft, entity);
      // Portal confirm still calls start without the contacts settings step;
      // keep the previous default so the backend receives
      // duplicate_policy: update.
      body =
        Object.keys(policies).length > 0
          ? policies
          : { duplicate_policy: 'update' };
    }
    try {
      return await ImporterService.startJob(jobId, body);
    } catch (error) {
      // The reason lets the wizard map the failure to a specific message and,
      // for an incomplete value mapping, send the user back to the right step.
      return rejectWithValue(rejectApiError(error));
    }
  },
  {
    // Guard against duplicate starts: skip while a start is in flight or the
    // job was already started successfully.
    condition: (_, { getState }) => {
      const { startStatus } = getState().ImporterSlice;
      return startStatus !== 'loading' && startStatus !== 'succeeded';
    },
  }
);

export const removeUploadedFileAsync = createAsyncThunk(
  'Importer/removeUploadedFile',
  async ({ index, fileId }, { getState }) => {
    const { jobId } = getState().ImporterSlice;
    if (fileId != null) {
      await ImporterService.deleteJobFile(jobId, fileId);
    }
    return { index, fileId };
  }
);

export const fetchMappingAsync = createAsyncThunk(
  'Importer/fetchMapping',
  async (_, { getState, rejectWithValue }) => {
    const { jobId, entity } = getState().ImporterSlice;
    try {
      const response = await ImporterService.getMapping(jobId, entity);
      return normalizeMapping(response, entity);
    } catch (error) {
      return rejectWithValue({
        message: error.message,
        reason: error.response?.reason ?? null,
      });
    }
  },
  {
    condition: (_, { getState }) => {
      const { mappingStatus, jobId, entity } = getState().ImporterSlice;
      return Boolean(jobId && entity) && mappingStatus !== 'loading';
    },
  }
);

export const saveMappingAsync = createAsyncThunk(
  'Importer/saveMapping',
  async (arg, { getState, rejectWithValue }) => {
    const { sourceColumnIndex, targetField, ignoreUnmapped } = arg ?? {};
    const { jobId, entity, mapping } = getState().ImporterSlice;
    const nextRows = ignoreUnmapped
      ? ignoreReviewRows(mapping.rows, mapping.catalog)
      : applyTargetFieldChange(
          mapping.rows,
          sourceColumnIndex,
          targetField,
          mapping.catalog
        );
    try {
      const response = await ImporterService.saveMapping(jobId, {
        entity,
        mapping: buildMappingPayload(nextRows),
      });
      return normalizeMapping(response, entity);
    } catch (error) {
      return rejectWithValue({
        message: error.message,
        reason: error.response?.reason ?? null,
      });
    }
  }
);

// Agents and lead statuses come from a single endpoint, so both steps read the
// same response and each one saves only its own block.
export const fetchValueMappingsAsync = createAsyncThunk(
  'Importer/fetchValueMappings',
  async (_, { getState, rejectWithValue }) => {
    const { jobId } = getState().ImporterSlice;
    try {
      const response = await ImporterService.getValueMappings(jobId);
      return normalizeValueMappings(response);
    } catch (error) {
      return rejectWithValue(rejectApiError(error));
    }
  },
  {
    condition: (_, { getState }) => {
      const { jobId, valueMappingsStatus } = getState().ImporterSlice;
      return Boolean(jobId) && valueMappingsStatus !== 'loading';
    },
  }
);

export const saveAgentMappingsAsync = createAsyncThunk(
  'Importer/saveAgentMappings',
  async (_, { getState, rejectWithValue }) => {
    const { jobId, valueMappings, agentMappingsDraft, agentDefaultDraft } =
      getState().ImporterSlice;
    const block = valueMappings?.agents;
    if (!block) {
      return rejectWithValue({ message: 'Agent mappings are not loaded.' });
    }
    try {
      const body = buildAgentMappingsBody(
        block,
        agentMappingsDraft,
        agentDefaultDraft
      );
      const response = await ImporterService.saveAgentMappings(jobId, body);
      return normalizeValueMappings({
        agents: response.agents,
        lead_status: valueMappings.lead_status,
        ready_for_start: response.ready_for_start,
      });
    } catch (error) {
      return rejectWithValue(rejectApiError(error));
    }
  }
  // No `condition` guard here: saving now fires on every dropdown change, so
  // gating on valueMappingsSaveStatus === 'loading' would silently drop an
  // edit made while a previous save is still in flight. Overlapping requests
  // are fine — the reducer only applies the most recently dispatched one's
  // response (see agentMappingsSaveRequestId in ImporterSlice).
);

export const saveLeadStatusMappingsAsync = createAsyncThunk(
  'Importer/saveLeadStatusMappings',
  async (_, { getState, rejectWithValue }) => {
    const {
      jobId,
      valueMappings,
      leadStatusMappingsDraft,
      leadStatusDefaultDraft,
    } = getState().ImporterSlice;
    const block = valueMappings?.lead_status;
    if (!block) {
      return rejectWithValue({
        message: 'Lead status mappings are not loaded.',
      });
    }
    try {
      const body = buildLeadStatusMappingsBody(
        block,
        leadStatusMappingsDraft,
        leadStatusDefaultDraft
      );
      const response = await ImporterService.saveLeadStatusMappings(
        jobId,
        body
      );
      return normalizeValueMappings({
        agents: valueMappings.agents,
        lead_status: response.lead_status,
        ready_for_start: response.ready_for_start,
      });
    } catch (error) {
      return rejectWithValue(rejectApiError(error));
    }
  }
  // Same reasoning as saveAgentMappingsAsync: no condition guard, so a rapid
  // edit isn't dropped while an earlier save for this block is in flight.
);
